import { debounce } from "lodash";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as SendLogo } from "../../Assets/arrow-up-solid.svg";
import { ReactComponent as ReplyIcon } from "../../Assets/reply-solid.svg";
import { ReactComponent as XIcon } from "../../Assets/xmark-sharp-solid.svg";
import { activateOnKey } from "../../Helper/a11y";
import { registerComposerAttach } from "../../Helper/ComposerAttach";
import { AttachFileIcon } from "../AnimatedIcons/AnimatedIcons";
import {
  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE,
  isConversationAttachmentTooLarge,
} from "../../Helper/ConversationAttachmentUpload";
import { resizeFile } from "../../Helper/ImageResize";
import { focusWithoutScroll } from "../../Helper/ScrollHelper";
import { useConfigStore } from "../../Stores/Config";
import { useTicketStore } from "../../Stores/Ticket";
import { getMessageAuthorName } from "../../Helper/MessageAuthorName";
import { getQuotePreviewText } from "../ChatMessage/ChatMessage";
import AudioRecorderUpload from "../AudioRecorderUpload/AudioRecorderUpload";
import { KaiVoiceButton } from "../KaiVoiceButton/KaiVoiceButton";
import ComposerShell from "../ComposerShell/ComposerShell";
import FormLoading from "../FormLoading/FormLoading";

export const defaultAttachmentMessage = "📎 Attachment";

const allowedTypes = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
]);

const ChatMessageComposer = forwardRef<
  { setFiles: (files: File[]) => void },
  {
    sendMessage: (message: string, attachments: File[]) => void;
    onHeightUpdated: (height: number) => void;
    answerBotIsActive?: boolean;
    disabled?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
  }
>(function ChatMessageComposer(
  {
    sendMessage,
    onHeightUpdated,
    answerBotIsActive = false,
    disabled = false,
    onFocus,
    onBlur,
  },
  ref,
) {
  const config = useConfigStore();
  const uploadingAttachment = useTicketStore(
    (store) => store.uploadingAttachment,
  );
  const setCurrentComposerText = useTicketStore(
    (store) => store.setCurrentComposerText,
  );
  const currentComposerText = useTicketStore(
    (store) => store.currentComposerText,
  );
  const replyingTo = useTicketStore((store) => store.replyingTo);
  const setReplyingTo = useTicketStore((store) => store.setReplyingTo);
  const textareaRef = useRef(null as any);
  const composerContainerRef = useRef<HTMLDivElement>(null);
  const imageResizerPickerRef = useRef(null as any);
  const [queue, setQueue] = useState<any[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const [audioRecordingIsActive, setAudioRecordingIsActive] = useState(false);
  const botWrittingText = useTicketStore((store) => store.botWrittingText);
  const botWrittingIsThinking = useTicketStore(
    (store) => store.botWrittingIsThinking,
  );

  const hasUploading = queue.some((q) => q.status === "uploading");
  const hasReadyAttachment = queue.some((q) => q.status === "done" && q.url);
  const isDisabled =
    disabled ||
    hasUploading ||
    (botWrittingText && botWrittingText.length > 0) ||
    !(
      (currentComposerText && currentComposerText.trim().length > 0) ||
      hasReadyAttachment
    );

  const queueRef = useRef<any[]>([]);
  queueRef.current = queue;
  const uploadChainRef = useRef<Promise<any>>(Promise.resolve());

  const startUpload = (id: string, file: File) => {
    uploadChainRef.current = uploadChainRef.current
      .catch(() => {})
      .then(() => {
        if (!queueRef.current.some((q) => q.id === id)) return;
        return useTicketStore
          .getState()
          .uploadFile(file, (pct) => {
            setQueue((prev) =>
              prev.map((q) => (q.id === id ? { ...q, progress: pct } : q)),
            );
          })
          .then((url) => {
            setQueue((prev) =>
              prev.map((q) =>
                q.id === id ? { ...q, status: "done", progress: 100, url } : q,
              ),
            );
          })
          .catch((err) => {
            console.warn("Attachment upload failed:", err);
            setAttachmentError(
              err?.message || "Upload failed. Please try again.",
            );
            setQueue((prev) => prev.filter((q) => q.id !== id));
          });
      });
  };

  const setFilesSmart = (files: File[]) => {
    if (!files || files.length === 0) {
      setQueue([]);
      setAttachmentError(undefined);
      focusWithoutScroll(textareaRef.current);
      return;
    }

    const filesToUpload = files.filter((file) => {
      const canResizeBeforeUpload =
        answerBotIsActive && file?.type?.startsWith("image");
      return canResizeBeforeUpload || !isConversationAttachmentTooLarge(file);
    });

    if (filesToUpload.length < files.length) {
      setAttachmentError(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE);
    } else {
      setAttachmentError(undefined);
    }

    if (filesToUpload.length === 0) {
      focusWithoutScroll(textareaRef.current);
      return;
    }

    const newItems = filesToUpload.map((file) => ({
      id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      file,
      status: "uploading" as const,
      progress: 0,
      url: undefined as string | undefined,
    }));

    setQueue((prev) => [...prev, ...newItems]);

    newItems.forEach((item) => {
      resizeFile(item.file, answerBotIsActive)
        .then((resized) => {
          const finalFile =
            resized && typeof resized !== "string" ? resized : item.file;
          if (resized && typeof resized !== "string" && resized !== item.file) {
            setQueue((prev) =>
              prev.map((q) =>
                q.id === item.id ? { ...q, file: finalFile } : q,
              ),
            );
          }
          if (isConversationAttachmentTooLarge(finalFile)) {
            setAttachmentError(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE);
            setQueue((prev) => prev.filter((q) => q.id !== item.id));
            return;
          }
          startUpload(item.id, finalFile);
        })
        .catch((err) => {
          console.warn("Attachment preparation failed:", err);
          if (isConversationAttachmentTooLarge(item.file)) {
            setAttachmentError(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE);
            setQueue((prev) => prev.filter((q) => q.id !== item.id));
            return;
          }
          startUpload(item.id, item.file);
        });
    });

    focusWithoutScroll(textareaRef.current);
  };

  const removeQueued = (id: string) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const debouncedUserTypingPing = useCallback(
    debounce(
      (text: any) => {
        var isTyping = false;

        if (text && text.length > 0) {
          isTyping = true;
        }

        useTicketStore.getState().isTyping(isTyping);
      },
      800,
      {
        leading: true,
        trailing: false,
      },
    ),
    [],
  );

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      // Measure at `auto` (the rows=1 box), NEVER by collapsing to 0px: on
      // iPad WKWebView a zero-height textarea scrolls its content one line to
      // keep the caret "visible", and UIKit keeps drawing the caret at that
      // scrolled position after the height is restored — the cursor renders a
      // full line below the text and the send button's tap area drifts with it
      // (ticket 146595). `auto` measures the same content height.
      textareaRef.current.style.height = "auto";
      var scrollHeight = textareaRef.current.scrollHeight;
      if (scrollHeight < 50) {
        scrollHeight = 50;
      }

      textareaRef.current.style.height = scrollHeight + "px";
    }

    // Report the FULL composer height (textarea + the toolbar row below it,
    // plus attachment previews) so the message list reserves enough bottom
    // space and the last message isn't hidden behind the floating composer.
    const fullHeight = composerContainerRef.current?.offsetHeight;
    if (fullHeight) {
      onHeightUpdated(fullHeight);
    }
  }, [
    currentComposerText,
    audioRecordingIsActive,
    queue,
    attachmentError,
    replyingTo,
  ]);

  useImperativeHandle(ref, () => ({
    setFiles: (files: File[]) => {
      setFilesSmart(files);
    },
  }));

  const internalSendMessage = () => {
    if (isDisabled) {
      return;
    }

    const attachments = queue
      .filter((q) => q.status === "done" && q.url)
      .map((q) => ({ url: q.url, name: q.file.name, type: q.file.type }));

    sendMessage(
      currentComposerText && currentComposerText.trim().length > 0
        ? currentComposerText
        : defaultAttachmentMessage,
      attachments as any,
    );
    setCurrentComposerText("");
    setQueue([]);
    setAttachmentError(undefined);
    imageResizerPickerRef.current?.clear();
  };

  const handleFileInputClick = () => {
    var inputElement = document.createElement("input");
    inputElement.style.display = "none";
    if (document.body) {
      document.body.appendChild(inputElement);
    }
    inputElement.type = "file";
    inputElement.accept =
      ".json,.pdf,.csv,.txt,.xlsx,.mp4,.mp3,.mov,.webp,.webm,image/jpeg,image/png,image/jpg,image/webp,image/webm,image/gif,.zip,.log";
    inputElement.multiple = true;
    inputElement.addEventListener("change", (arg) => {
      if (inputElement.files && inputElement.files.length > 0) {
        setFilesSmart(Array.from(inputElement.files));
      }

      // Safely remove the input element using remove() method
      // This works even if the element is no longer a child of document.body
      if (inputElement.parentNode) {
        inputElement.remove();
      }
    });
    inputElement.dispatchEvent(new MouseEvent("click"));
  };

  // "Share one" on a skipped capture card opens this picker.
  const canAttach =
    !(answerBotIsActive && !config.config?.vision) &&
    !config.config?.disableAttachments;
  const filePickerRef = useRef(handleFileInputClick);
  filePickerRef.current = handleFileInputClick;
  useEffect(() => {
    if (!canAttach) {
      return;
    }
    return registerComposerAttach(() => filePickerRef.current());
  }, [canAttach]);

  const handlePasteCapture = (e: any) => {
    if (!e.clipboardData) {
      return;
    }

    const items = e.clipboardData.items;
    if (!items || items.length === 0) {
      return;
    }

    let files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (
        item &&
        (item.type.startsWith("image") ||
          (!answerBotIsActive && allowedTypes.has(item.type)))
      ) {
        const blob = item.getAsFile();
        if (blob) {
          files.push(blob);
        }
      }
    }

    if (files.length > 0) {
      setFilesSmart(files);
      e.preventDefault();
    }
  };

  const onEnterPress = (e: any) => {
    const sendOnEnter = config.config?.sendOnEnter ?? true;
    if (sendOnEnter) {
      if (e.keyCode == 13 && e.shiftKey == false) {
        e.preventDefault();
        internalSendMessage();
      }
    } else {
      if (e.keyCode == 13 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        internalSendMessage();
      }
    }
  };

  const renderAttachment = () => {
    if (answerBotIsActive && !config.config?.vision) {
      return null;
    }

    if (config.config?.disableAttachments) {
      return null;
    }

    if (uploadingAttachment) {
      return (
        <div className="attachment-loading">
          <FormLoading />
        </div>
      );
    }

    return (
      <AttachFileIcon
        className="attachment-button"
        role="button"
        tabIndex={0}
        aria-label={config.translateTextWithFallback(
          "attachFile",
          "Attach file",
        )}
        onClick={handleFileInputClick}
        onKeyDown={activateOnKey(handleFileInputClick)}
      />
    );
  };

const aiIsWorking = !!(
    botWrittingIsThinking ||
    (botWrittingText && botWrittingText.length > 0)
  );

  // "Replying to …" strip docked at the top of the composer card while a
  // quoted-reply target is selected (the store's sendMessage attaches
  // replyTo and clears it).
  const replyBanner = replyingTo ? (
    <div className="replying-to-banner">
      <ReplyIcon className="replying-to-banner-icon" />
      <div className="replying-to-banner-content">
        <div className="replying-to-banner-name">
          {getMessageAuthorName(replyingTo)}
        </div>
        <div className="replying-to-banner-text">
          {getQuotePreviewText(replyingTo)}
        </div>
      </div>
      <div
        className="replying-to-banner-close"
        role="button"
        tabIndex={0}
        aria-label="Cancel reply"
        onClick={() => {
          setReplyingTo(undefined);
        }}
        onKeyDown={activateOnKey(() => {
          setReplyingTo(undefined);
        })}
      >
        <XIcon />
      </div>
    </div>
  ) : undefined;

  return (
    <>
      <div className="chat-message-composer-bg"></div>
<ComposerShell
        ref={composerContainerRef}
        className={aiIsWorking ? "chat-message-composer--ai-working" : ""}
        audioActive={audioRecordingIsActive}
        errorMessage={attachmentError}
        onDismissError={() => setAttachmentError(undefined)}
        attachments={queue}
        onRemoveAttachment={removeQueued}
        banner={replyBanner}
        textarea={
          <textarea
            spellCheck={false}
            value={currentComposerText}
            aria-label={config.translateText("writeReply")}
            placeholder={config.translateText("writeReply")}
            ref={textareaRef}
            onChange={(e) => {
              setCurrentComposerText(e.target.value);
              debouncedUserTypingPing(e.target.value);
            }}
            onPasteCapture={handlePasteCapture}
            onKeyDown={onEnterPress}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        }
      >
        {!audioRecordingIsActive &&
          queue?.length === 0 &&
          (currentComposerText ?? "").trim().length === 0 && <KaiVoiceButton />}
        {!audioRecordingIsActive && renderAttachment()}
        {queue?.length > 0 ? null : (
          <AudioRecorderUpload
            onAudioRecordingStart={() => {
              setAudioRecordingIsActive(true);
            }}
            onAudioRecordingStop={() => {
              setAudioRecordingIsActive(false);
            }}
            onSend={(attachment) => {
              // The recorder uploads the recording itself (server transcodes
              // to MP3) and hands back a ready attachment descriptor. Size is
              // validated on the raw recording inside the recorder.
              setAttachmentError(undefined);
              sendMessage(defaultAttachmentMessage, [attachment] as any);
            }}
          />
        )}
        {!audioRecordingIsActive && (
          <div
            role="button"
            tabIndex={0}
            aria-label={config.translateTextWithFallback(
              "sendMessage",
              "Send message",
            )}
            aria-disabled={isDisabled}
            onClick={() => {
              internalSendMessage();
            }}
            onKeyDown={activateOnKey(() => {
              internalSendMessage();
            })}
            className={`send-button ${isDisabled && `send-button--disabled`} `}
          >
            <SendLogo />
          </div>
        )}
      </ComposerShell>
    </>
  );
});

export default ChatMessageComposer;
